import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Button, Box, Paper, Chip,
  Stack, Alert, CircularProgress, Divider, Avatar,
  TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaceIcon from '@mui/icons-material/Place';
import LinkIcon from '@mui/icons-material/Link';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useParams, useNavigate } from 'react-router-dom';
import Map, { Marker, NavigationControl, FullscreenControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import StarRating from '../components/StarRating';
import PhotoUpload from '../components/PhotoUpload';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { getTrip, saveRating, deleteActivity, updateActivity, voteActivity, reactToActivity, getTripMembers } from '../services/api';
import { useAuth } from '../App';
import config from '../config';

const CATEGORY_COLORS = {
  restaurant: '#e91e63',
  hotel: '#9c27b0',
  attraction: '#2196f3',
  activity: '#4caf50',
  other: '#ff9800',
};

export default function ActivityDetail() {
  const { tripId, activityId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [savingRating, setSavingRating] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [photoUrls, setPhotoUrls] = useState([]);
  const [memberMap, setMemberMap] = useState({});

  useEffect(() => { loadData(); }, [tripId, activityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const { data } = await getTrip(tripId);
      setTrip(data);
      const act = data.activities?.find((a) => a.activityId === activityId);
      if (act) {
        setActivity(act);
        setPhotoUrls(act.photoUrls || []);
        const existing = act.ratings?.find((r) => r.userId === user?.userId);
        if (existing) {
          setMyRating(existing.stars);
          setMyComment(existing.comment || '');
        }
      }
      getTripMembers(tripId).then(({ data: members }) => {
        const map = {};
        members.forEach((m) => {
          map[m.userId] = m;
          if (m.username) map[m.username] = m;
          if (m.displayName) map[m.displayName] = m;
        });
        setMemberMap(map);
      }).catch(() => {});
    } catch {
      setError('Failed to load activity.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRating = async () => {
    if (!myRating) return;
    setSavingRating(true);
    try {
      await saveRating(tripId, activityId, {
        stars: myRating,
        comment: myComment,
        userName: user?.userDetails || 'Family Member',
      });
      navigate(`/trips/${tripId}`);
    } catch {
      setError('Failed to save rating.');
      setSavingRating(false);
    }
  };

  const handlePhotosChange = async (urls) => {
    setPhotoUrls(urls);
    await updateActivity(tripId, activityId, { ...activity, photoUrls: urls });
  };

  const handleVote = async () => {
    try {
      await voteActivity(tripId, activityId);
      await loadData();
    } catch {
      // silently ignore
    }
  };

  const handleReact = async (emoji) => {
    try {
      await reactToActivity(tripId, activityId, emoji);
      await loadData();
    } catch {
      // silently ignore
    }
  };

  const handleDelete = async () => {
    try {
      await deleteActivity(tripId, activityId);
      navigate(`/trips/${tripId}`);
    } catch {
      setError('Failed to delete activity.');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;
  if (error) return <Container sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!activity) return null;

  const avgRating = activity.ratings?.length > 0
    ? activity.ratings.reduce((s, r) => s + r.stars, 0) / activity.ratings.length
    : 0;

  const voteCount = activity.votes?.length || 0;
  const hasVoted = user?.userId && activity.votes?.some((v) => v.userId === user.userId);

  const catColor = CATEGORY_COLORS[activity.category] || '#ff9800';
  const hasLocation = activity.location?.lat && activity.location?.lng;

  return (
    <Box>
      {/* Map Hero */}
      {hasLocation && (
        <Box sx={{ height: 300, position: 'relative' }}>
          <Map
            mapboxAccessToken={config.mapboxToken}
            initialViewState={{ latitude: activity.location.lat, longitude: activity.location.lng, zoom: 14 }}
            style={{ width: '100%', height: '100%' }}
            mapStyle={config.mapStyle}
          >
            <NavigationControl position="top-right" />
            <FullscreenControl position="top-right" />
            <Marker latitude={activity.location.lat} longitude={activity.location.lng} anchor="bottom">
              <Box
                sx={{
                  bgcolor: catColor,
                  borderRadius: '50% 50% 50% 0',
                  transform: 'rotate(-45deg)',
                  width: 40, height: 40,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: 3,
                }}
              >
                <PlaceIcon sx={{ transform: 'rotate(45deg)', color: 'white', fontSize: 22 }} />
              </Box>
            </Marker>
          </Map>

          {/* Overlay buttons */}
          <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 1 }}>
            <Button
              variant="contained" size="small" startIcon={<ArrowBackIcon />}
              onClick={() => navigate(`/trips/${tripId}`)}
              sx={{ bgcolor: 'rgba(0,0,0,0.7)', color: '#fff', boxShadow: 3, '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' } }}
            >
              Back
            </Button>
          </Box>
          <Box sx={{ position: 'absolute', top: 12, right: 56, display: 'flex', gap: 1 }}>
            <IconButton
              size="small" sx={{ bgcolor: 'rgba(0,0,0,0.7)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' } }}
              onClick={() => navigate(`/trips/${tripId}/activities/${activityId}/edit`)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small" sx={{ bgcolor: 'rgba(0,0,0,0.7)', color: '#ef5350', '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' } }}
              onClick={() => setDeleteOpen(true)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      <Container maxWidth="md" sx={{ mt: hasLocation ? -3 : 4, mb: 6, position: 'relative', zIndex: 1 }}>

        {/* No location — show back/edit buttons normally */}
        {!hasLocation && (
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/trips/${tripId}`)}>Back</Button>
            <Box>
              <IconButton onClick={() => navigate(`/trips/${tripId}/activities/${activityId}/edit`)}><EditIcon /></IconButton>
              <IconButton color="error" onClick={() => setDeleteOpen(true)}><DeleteIcon /></IconButton>
            </Box>
          </Box>
        )}

        {/* Activity Info Card */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Typography variant="h4" fontWeight="bold">{activity.name}</Typography>
            <Chip label={activity.category} size="small" sx={{ bgcolor: catColor, color: 'white', ml: 1, mt: 0.5 }} />
          </Box>

          {activity.location?.address && (
            <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
              <PlaceIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography color="text.secondary" variant="body2">{activity.location.address}</Typography>
            </Stack>
          )}

          {activity.websiteUrl && (
            <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
              <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography
                component="a"
                href={activity.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                color="primary"
                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {(() => { try { return new URL(activity.websiteUrl).hostname; } catch { return activity.websiteUrl; } })()}
              </Typography>
            </Stack>
          )}

          {activity.date && (
            <Stack direction="row" alignItems="center" spacing={0.5} mb={2}>
              <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {new Date(activity.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </Typography>
            </Stack>
          )}

          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <StarRating value={Math.round(avgRating)} size="large" />
            <Typography variant="body2" color="text.secondary">
              {avgRating > 0 ? `${avgRating.toFixed(1)} avg` : 'No ratings yet'} ({activity.ratings?.length || 0})
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5} mb={1}>
            <IconButton
              size="small"
              onClick={handleVote}
              sx={{ color: hasVoted ? 'error.main' : 'text.disabled' }}
            >
              <FavoriteIcon />
            </IconButton>
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              {voteCount} {voteCount === 1 ? 'vote' : 'votes'}
            </Typography>
          </Box>

          {/* Emoji Reactions */}
          <Box display="flex" gap={1} flexWrap="wrap" mb={activity.notes ? 2 : 0}>
            {['\uD83D\uDD25', '\uD83D\uDE0D', '\uD83D\uDC4D', '\uD83D\uDE02', '\uD83C\uDF89', '\u2764\uFE0F'].map((emoji) => {
              const reaction = activity.reactions?.find((r) => r.emoji === emoji);
              const count = reaction?.userIds?.length || 0;
              const hasReacted = user?.userId && (reaction?.userIds?.includes(user.userId) || false);
              return (
                <Chip
                  key={emoji}
                  label={count > 0 ? `${emoji} ${count}` : emoji}
                  size="small"
                  variant={hasReacted ? 'filled' : 'outlined'}
                  onClick={handleReact.bind(null, emoji)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: hasReacted ? 'bold' : 'normal',
                    bgcolor: hasReacted ? 'action.selected' : undefined,
                  }}
                />
              );
            })}
          </Box>

          {activity.notes && (
            <Typography sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
              "{activity.notes}"
            </Typography>
          )}
        </Paper>

        {/* Photos */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>Photos</Typography>
          <PhotoUpload tripId={tripId} activityId={activityId} photoUrls={photoUrls} onPhotosChange={handlePhotosChange} canDelete={trip?.createdBy === user?.userId} />
        </Paper>

        {/* Rate It */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>Your Rating</Typography>
          <StarRating value={myRating} onChange={setMyRating} size="large" />
          <TextField
            fullWidth multiline rows={2} label="Comment (optional)"
            value={myComment} onChange={(e) => setMyComment(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Button variant="contained" sx={{ mt: 2 }} onClick={handleSaveRating} disabled={!myRating || savingRating}>
            {savingRating ? 'Saving...' : 'Save Rating'}
          </Button>
        </Paper>

        {/* Family Ratings */}
        {activity.ratings?.length > 0 && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Family Ratings</Typography>
            <Stack spacing={2} divider={<Divider />}>
              {activity.ratings.map((r, i) => {
                const member = memberMap[r.userId] || memberMap[r.userName];
                return (
                <Box key={i} display="flex" gap={2}>
                  <Avatar
                    src={member?.profilePhotoUrl || undefined}
                    sx={{ bgcolor: member?.avatarColor || catColor, width: 36, height: 36, fontSize: 14 }}
                  >
                    {(member?.displayName || r.userName || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                  </Avatar>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight="bold" variant="body2">{r.userName}</Typography>
                      <StarRating value={r.stars} size="small" />
                    </Box>
                    {r.comment && <Typography variant="body2" color="text.secondary">{r.comment}</Typography>}
                  </Box>
                </Box>
                );
              })}
            </Stack>
          </Paper>
        )}
      </Container>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Activity?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete "{activity.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
